import { useMemo, useState, useEffect, useCallback } from 'react';
import { Box, Typography, styled } from '@mui/material';
import Chart from 'react-apexcharts';
import RenderChartLegend from '../../../AdvancedAnalytics/ContinuousChart/RenderChartLegend';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import CartLoader from '../../../../Helper/Loaders/CartLoader';
import { getFleetEnergyEvents } from '../../../../Api/PerformanceApi';
import { toTitleCase } from '../../../../Helper/Formatters/TextFormatter';
import { splitMidnightCrossingEvents } from '../../../DigitalTwinV3/Tabs/BatteryPerformance/CommonFunctions';

dayjs.extend(advancedFormat);

const SCROLLBAR_WIDTH = '8px';

const ChartContainer = styled(Box)({
    width: '100%',
    display: 'flex',
    gap: '12px',
    '&::-webkit-scrollbar': { width: SCROLLBAR_WIDTH },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#D1D1D1', borderRadius: '4px' },
});

const LabelsContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    width: '80px',
    flexShrink: 0,
    height: '100%',
});

const ChartWrapper = styled(Box)({
    flex: 1,
    minWidth: 0,
});

const EVENT_COLORS = {
    discharging: '#006A97',
    charging: '#09B820',
    idle: '#FFCA38',
    NO_DATA: '#E6E6E6',
};

const transformEventsToTimeline = (events, startDate, endDate) => {
    const msInDay = 24 * 60 * 60 * 1000;
    const groupedByDate = events.reduce((acc, event) => {
        const dateKey = dayjs(event.startTime, "YYYY-MM-DD HH:mm:ss").format('Do MMM YYYY');
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(event);
        return acc;
    }, {});

    let allDates = Object.keys(groupedByDate);
    if (startDate && endDate) {
        const start = dayjs(startDate, "YYYY-MM-DD");
        const end = dayjs(endDate, "YYYY-MM-DD");
        const dateRange = [];
        let currentDate = start;
        while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
            const dateKey = currentDate.format('Do MMM YYYY');
            dateRange.push(dateKey);
            if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
            currentDate = currentDate.add(1, 'day');
        }
        allDates = dateRange;
    }

    const sortedDates = allDates.sort((a, b) => {
        return dayjs(b, 'Do MMM YYYY').valueOf() - dayjs(a, 'Do MMM YYYY').valueOf();
    });

    const dischargeSeries = [];
    const chargeSeries = [];
    const noDataSeries = [];

    sortedDates.forEach(dateLabel => {
        const dayEvents = groupedByDate[dateLabel];
        if (!dayEvents || dayEvents.length === 0) {
            noDataSeries.push({ x: dateLabel, y: [0, msInDay], fillColor: EVENT_COLORS.NO_DATA });
            return;
        }

        const sortedEvents = dayEvents.sort((a, b) =>
            dayjs(a.startTime, "YYYY-MM-DD HH:mm:ss").diff(dayjs(b.startTime, "YYYY-MM-DD HH:mm:ss"))
        );

        const eventDataPoints = sortedEvents.map(event => {
            const startTime = dayjs(event.startTime, "YYYY-MM-DD HH:mm:ss");
            const endTime = dayjs(event.endTime, "YYYY-MM-DD HH:mm:ss");
            const startOfDay = startTime.startOf('day');
            return {
                type: event.eventType,
                startOffset: startTime.diff(startOfDay),
                endOffset: endTime.diff(startOfDay),
                startSoc: event.startSoc,
                endSoc: event.endSoc,
                chargeType: event.chargeType,
                chargeEnergyConsumed: event.chargeEnergyConsumed,
                dischargeEnergyConsumed: event.dischargeEnergyConsumed,
                distance: event.distance,
                regenEnergy: event.regenEnergy,
                kwhPerSoc: event.kwhPerSoc,
                kmPerSoc: event.kmPerSoc,
            };
        });

        let currentTime = 0;
        eventDataPoints.forEach(eventPoint => {
            if (currentTime < eventPoint.startOffset) {
                noDataSeries.push({ x: dateLabel, y: [currentTime, eventPoint.startOffset], fillColor: EVENT_COLORS.NO_DATA });
            }
            const dataPoint = {
                x: dateLabel,
                y: [eventPoint.startOffset, eventPoint.endOffset],
                fillColor: EVENT_COLORS[eventPoint.type] || EVENT_COLORS.NO_DATA,
                startSoc: eventPoint.startSoc,
                endSoc: eventPoint.endSoc,
                chargeType: eventPoint.chargeType,
                chargeEnergyConsumed: eventPoint.chargeEnergyConsumed,
                dischargeEnergyConsumed: eventPoint.dischargeEnergyConsumed,
                distance: eventPoint.distance,
                regenEnergy: eventPoint.regenEnergy,
                kwhPerSoc: eventPoint.kwhPerSoc,
                kmPerSoc: eventPoint.kmPerSoc,
            };
            if (eventPoint.type.toLowerCase() === 'discharging') dischargeSeries.push(dataPoint);
            else if (eventPoint.type.toLowerCase() === 'charging') chargeSeries.push(dataPoint);
            currentTime = eventPoint.endOffset;
        });

        if (currentTime < msInDay) {
            noDataSeries.push({ x: dateLabel, y: [currentTime, msInDay], fillColor: EVENT_COLORS.NO_DATA });
        }
    });

    return {
        categories: sortedDates,
        series: [
            { name: 'Discharging', data: dischargeSeries },
            { name: 'Charging', data: chargeSeries },
            { name: 'No Data', data: noDataSeries },
        ],
    };
};

const generateTimeAxisTicks = () => {
    const ticks = [];
    for (let hour = 0; hour <= 24; hour += 2) {
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour < 12 || hour === 24 ? 'AM' : 'PM';
        ticks.push({ value: hour, label: `${displayHour} ${period}` });
    }
    return ticks;
};

/**
 * Custom platform Daily Events chart.
 *
 * Accepts optional allowedEventTypes (e.g. ["charging", "discharging"]) to
 * filter which event types appear in the chart and legend. When omitted all
 * event types are shown.
 */
const CustomDailyEventsChart = ({ batteryID, startDate, endDate, allowedEventTypes }) => {
    const timeAxisTicks = useMemo(() => generateTimeAxisTicks(), []);
    const [categories, setCategories] = useState([]);
    const [series, setSeries] = useState([]);
    const [eventsData, setEventsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchEventsData = useCallback(async () => {
        try {
            setIsLoading(true);
            const fleetEventsResponse = await getFleetEnergyEvents({
                batteryID: [batteryID],
                startDate,
                endDate,
                skipPagination: true,
                ...(allowedEventTypes?.length && { eventType: allowedEventTypes }),
            });
            const rawData = fleetEventsResponse?.response?.data ?? [];
            const data = splitMidnightCrossingEvents(rawData);
            setEventsData(data);
        } catch (error) {
            console.error('CustomDailyEventsChart: error fetching events:', error);
            setEventsData([]);
        } finally {
            setIsLoading(false);
        }
    }, [batteryID, startDate, endDate, allowedEventTypes]);

    useEffect(() => {
        if (batteryID && startDate && endDate) fetchEventsData();
    }, [batteryID, startDate, endDate, fetchEventsData]);

    useEffect(() => {
        if (!isLoading) {
            const result = transformEventsToTimeline(eventsData, startDate, endDate);
            setCategories(result.categories);
            setSeries(result.series);
        }
    }, [eventsData, isLoading, startDate, endDate]);

    const fixedBarHeight = 24;
    const barGap = 30;
    const numBars = categories?.length || 0;
    const labelHeight = fixedBarHeight + barGap;
    const totalPadding = 20;
    const contentHeight = numBars * labelHeight + totalPadding;
    const minVisibilityHeight = 300;
    let chartHeight = contentHeight;
    let extraBottomPadding = 0;
    if (contentHeight < minVisibilityHeight) {
        extraBottomPadding = minVisibilityHeight - contentHeight;
        chartHeight = minVisibilityHeight;
    }

    const msInDay = 24 * 60 * 60 * 1000;

    const chartOptions = {
        chart: {
            id: 'custom-daily-events-chart',
            type: 'bar',
            height: chartHeight,
            toolbar: { show: false },
            zoom: { enabled: false },
            sparkline: { enabled: true },
            offsetX: 0,
            offsetY: 0,
            animations: { enabled: false },
        },
        plotOptions: {
            bar: { horizontal: true, rangeBarGroupRows: true, barHeight: `${fixedBarHeight}px`, borderRadius: 0 },
        },
        colors: [EVENT_COLORS.discharging, EVENT_COLORS.charging, EVENT_COLORS.NO_DATA],
        fill: { opacity: 1 },
        xaxis: {
            type: 'numeric',
            min: 0,
            max: msInDay,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false },
            categories,
        },
        yaxis: { show: false },
        grid: { show: false, padding: { top: 0, bottom: extraBottomPadding, left: 0, right: 0 } },
        tooltip: {
            custom: function ({ seriesIndex, dataPointIndex, w }) {
                const data = w.config.series[seriesIndex].data[dataPointIndex];
                if (!data) return '';
                const seriesName = w.config.series[seriesIndex].name;
                const dateLabel = data.x;
                const eventColor = data.fillColor || w.config.colors[seriesIndex];
                const isNoData = seriesName?.toLowerCase() === 'no data';

                const formatTime = (msOffset) => {
                    const hours = Math.floor(msOffset / (60 * 60 * 1000));
                    const minutes = Math.floor((msOffset % (60 * 60 * 1000)) / (60 * 1000));
                    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
                    const period = hours < 12 ? 'AM' : 'PM';
                    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
                };
                const startTime = data.y?.[0] !== undefined ? formatTime(data.y[0]) : 'No data';
                const endTime = data.y?.[1] !== undefined ? formatTime(data.y[1]) : 'No data';
                const formatSoc = (soc) => (soc !== null && soc !== undefined) ? `${soc}%` : 'No data';

                const timeDetailsHtml = `
                    <hr style="margin: 8px 0; border: 0; border-top: 1px solid #E6E6E6;" />
                    <div><span style="font-size: 12px; color: #494949;">Start Time: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${startTime}</span></div>
                    <div><span style="font-size: 12px; color: #494949;">End Time: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${endTime}</span></div>
                `;

                const socDetailsHtml = !isNoData ? `
                    <hr style="margin: 8px 0; border: 0; border-top: 1px solid #E6E6E6;" />
                    <div><span style="font-size: 12px; color: #494949;">Start SOC: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${formatSoc(data.startSoc)}</span></div>
                    <div><span style="font-size: 12px; color: #494949;">End SOC: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${formatSoc(data.endSoc)}</span></div>
                ` : '';

                let eventSpecificHtml = '';
                if (seriesName?.toLowerCase() === 'charging') {
                    const chargeType = data.chargeType ? toTitleCase(data.chargeType) : 'No data';
                    const chargeEnergy = data.chargeEnergyConsumed != null ? `${data.chargeEnergyConsumed.toFixed(2)} kWh` : 'No data';
                    eventSpecificHtml = `
                        <hr style="margin: 8px 0; border: 0; border-top: 1px solid #E6E6E6;" />
                        <div><span style="font-size: 12px; color: #494949;">Charge Type: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${chargeType}</span></div>
                        <div><span style="font-size: 12px; color: #494949;">Charging Energy: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${chargeEnergy}</span></div>
                    `;
                } else if (seriesName?.toLowerCase() === 'discharging') {
                    const dischargeEnergy = data.dischargeEnergyConsumed != null ? `${data.dischargeEnergyConsumed.toFixed(2)} kWh` : 'No data';
                    const distance = data.distance != null ? `${data.distance.toFixed(2)} km` : 'No data';
                    const regenEnergy = data.regenEnergy != null ? `${data.regenEnergy.toFixed(2)} kWh` : 'No data';
                    const kmPerSoc = data.kmPerSoc != null ? `${parseFloat(data.kmPerSoc).toFixed(2)} km/SOC` : 'No data';
                    eventSpecificHtml = `
                        <hr style="margin: 8px 0; border: 0; border-top: 1px solid #E6E6E6;" />
                        <div><span style="font-size: 12px; color: #494949;">Discharging Energy: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${dischargeEnergy}</span></div>
                        <div><span style="font-size: 12px; color: #494949;">Odometer: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${distance}</span></div>
                        <div><span style="font-size: 12px; color: #494949;">Regeneration: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${regenEnergy}</span></div>
                        <div><span style="font-size: 12px; color: #494949;">km/SOC: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${kmPerSoc}</span></div>
                    `;
                }

                return `<div style="background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.1); min-width: 160px;">
                    <div style="font-weight: 600; font-size: 12px; color: #000000; background-color: #F3F5F7; padding: 8px 12px;">${dateLabel}</div>
                    <div style="padding: 12px">
                        <div style="display: flex; align-items: center; gap: 8px;">
                            <div style="width: 10px; height: 10px; border-radius: 50%; background-color: ${eventColor};"></div>
                            <div><span style="font-size: 12px; color: #494949;">Event: </span><span style="font-size: 12px; color: #494949; font-weight: 500;">${seriesName}</span></div>
                        </div>
                        ${timeDetailsHtml}${socDetailsHtml}${eventSpecificHtml}
                    </div>
                </div>`;
            }
        },
        legend: { show: false },
        dataLabels: { enabled: false },
        states: { hover: { filter: { type: 'darken', value: 0.1 } } },
    };

    const allLegendSeries = [
        { name: 'Discharging', color: EVENT_COLORS.discharging, type: 'discharging' },
        { name: 'Charging', color: EVENT_COLORS.charging, type: 'charging' },
        { name: 'No Data', color: EVENT_COLORS.NO_DATA, type: 'no_data' },
    ];
    const legendSeries = allowedEventTypes?.length
        ? allLegendSeries.filter(s => s.type === 'no_data' || allowedEventTypes.includes(s.type))
        : allLegendSeries;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#FFFFFF', borderRadius: '12px', border: '1px solid #E6E6E6', p: 3 }}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="fw600s16lh24c444B51" sx={{ fontWeight: 500, color: '#000000' }}>
                    Daily Events
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Box sx={{ flex: 1 }}>
                    <RenderChartLegend
                        legendSeries={legendSeries}
                        chartIDs={['custom-daily-events-chart']}
                        isLoading={isLoading}
                        centered={true}
                    />
                </Box>
            </Box>

            <ChartContainer>
                <LabelsContainer sx={{ paddingTop: `${totalPadding / 2}px`, paddingBottom: `${totalPadding / 2}px` }}>
                    {categories?.map((category, index) => (
                        <Box key={index} sx={{ height: `${labelHeight}px`, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ height: `${fixedBarHeight + 8}px`, width: '100%', display: 'flex', alignItems: 'center', borderRadius: '4px' }}>
                                <Typography
                                    variant="fw500s12lh24c686868"
                                    sx={{ lineHeight: '16px', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}
                                >
                                    {category}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </LabelsContainer>
                <ChartWrapper>
                    {!isLoading && series.length > 0 && (
                        <Chart options={chartOptions} series={series} type="rangeBar" height={chartHeight} width="100%" />
                    )}
                    {isLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: chartHeight }}>
                            <CartLoader />
                        </Box>
                    )}
                    {!isLoading && series.length === 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: chartHeight }}>
                            <Typography variant="fw400s12c3D4B5A">No events found</Typography>
                        </Box>
                    )}
                </ChartWrapper>
            </ChartContainer>

            <Box sx={{ display: 'flex', gap: '16px', width: '100%', marginTop: '8px', paddingRight: SCROLLBAR_WIDTH }}>
                <Box sx={{ width: '80px', flexShrink: 0 }} />
                <Box sx={{ flex: 1, position: 'relative', height: '30px', borderTop: '1px solid #E9EFF5', marginLeft: '5px', marginRight: '8px' }}>
                    {timeAxisTicks.map((tick, index) => (
                        <Box
                            key={tick.value}
                            sx={{
                                position: 'absolute',
                                left: `${(tick.value / 24) * 100}%`,
                                transform: index === 0 ? 'translateX(0%)' : index === timeAxisTicks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                                top: '4px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="fw500s10lh24c5E758D" sx={{ lineHeight: '16px', color: '#87939D', whiteSpace: 'nowrap' }}>
                                {tick.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default CustomDailyEventsChart;
